import assert from 'assert';

import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';

import {ComplexPolymerInterfaceQuery_complexPolymerSchemaConfiguration_GenericComplexPolymerSchemaConfigNode_OligoConjugateSchemaConfigNode_OligoDuplexSchemaConfigNode as ComplexPolymerSchemaInterfaceGQL} from './__generated/ComplexPolymerInterfaceQuery';
import {ComplexPolymerType} from './ComplexPolymerConstants';

// Keep in sync with `ComplexPolymerInterfaceApiId` in `benchling/modbio/complex_polymers/core/complex_polymer_schema_configuration.py`
export enum ComplexPolymerInterfaceApiId {
  GENERIC_COMPLEX_POLYMER_ID = 'ssi_genericcomplexpolymer',
  OLIGO_CONJUGATE_ID = 'ssi_oligoconjugate',
  OLIGO_DUPLEX_ID = 'ssi_oligoduplex',
}

// Indicates that the user intends to set up a complex polymer interface, but hasn't picked one yet.
// Should only be used for this transient state during schema configuration in the UI.
export const UNSET_COMPLEX_POLYMER_INTERFACE_ID = 'ssi_UNSET_COMPLEX_POLYMER';

const COMPLEX_POLYMER_INTERFACE_API_ID_TO_TYPE: {
  [key in ComplexPolymerInterfaceApiId]: ComplexPolymerType;
} = {
  [ComplexPolymerInterfaceApiId.GENERIC_COMPLEX_POLYMER_ID]: ComplexPolymerType.GENERIC,
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID]: ComplexPolymerType.OLIGO_CONJUGATE,
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID]: ComplexPolymerType.OLIGO_DUPLEX,
};

const COMPLEX_POLYMER_TYPE_TO_INTERFACE_API_ID: {
  [key in ComplexPolymerType]: ComplexPolymerInterfaceApiId;
} = {
  [ComplexPolymerType.GENERIC]: ComplexPolymerInterfaceApiId.GENERIC_COMPLEX_POLYMER_ID,
  [ComplexPolymerType.OLIGO_CONJUGATE]: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
  [ComplexPolymerType.OLIGO_DUPLEX]: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
};

export interface UnsetImplementedComplexPolymerInterface extends ImplementedStaticSchemaInterface {
  interfaceId: typeof UNSET_COMPLEX_POLYMER_INTERFACE_ID;
}

export interface ImplementedGenericComplexPolymerInterface extends ImplementedStaticSchemaInterface {
  interfaceId: ComplexPolymerInterfaceApiId.GENERIC_COMPLEX_POLYMER_ID;
}

export interface ComplexPolymerSchemaFieldSpec {
  schemaFieldName: string;
  schemaOrInterfaceId: string;
}

// Keep in sync with OLIGO_CONJUGATE_SHAPE in benchling/modbio/complex_polymers/api/rest/schema_config_params.py
export interface ImplementedOligoConjugateInterface extends ImplementedStaticSchemaInterface {
  interfaceId: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID;
  oligoSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  conjugatesSchemaFieldSpecs: Array<ComplexPolymerSchemaFieldSpec>;
}

// Keep in sync with OLIGO_DUPLEX_SHAPE in benchling/modbio/complex_polymers/api/rest/schema_config_params.py
export interface ImplementedOligoDuplexInterface extends ImplementedStaticSchemaInterface {
  interfaceId: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID;
  forwardStrandSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  reverseStrandSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  conjugatesSchemaFieldSpecs: Array<ComplexPolymerSchemaFieldSpec>;
}

export type DraftImplementedOligoConjugateInterface = Pick<
  ImplementedOligoConjugateInterface,
  'interfaceId'
> &
  Partial<ImplementedOligoConjugateInterface>;

export type DraftImplementedOligoDuplexInterface = Pick<ImplementedOligoDuplexInterface, 'interfaceId'> &
  Partial<ImplementedOligoDuplexInterface>;

export type DraftImplementedComplexPolymerInterface =
  | UnsetImplementedComplexPolymerInterface
  | ImplementedGenericComplexPolymerInterface
  | DraftImplementedOligoConjugateInterface
  | DraftImplementedOligoDuplexInterface;

export type ImplementedComplexPolymerInterface =
  | ImplementedGenericComplexPolymerInterface
  | ImplementedOligoConjugateInterface
  | ImplementedOligoDuplexInterface;

export type ComplexPolymerInterfaceFieldName =
  | keyof Omit<ImplementedOligoConjugateInterface, 'interfaceId'>
  | keyof Omit<ImplementedOligoDuplexInterface, 'interfaceId'>;

export const CLIENT_COMPLEX_POLYMER_INTERFACE_API_IDS: Array<string> = Object.values(
  ComplexPolymerInterfaceApiId
)
  .map((v) => v.toString())
  .concat([UNSET_COMPLEX_POLYMER_INTERFACE_ID]);

export function isComplexPolymerInterfaceId(interfaceId: string): boolean {
  return (
    Object.values(ComplexPolymerInterfaceApiId)
      .map((v) => v.toString())
      .includes(interfaceId) || interfaceId === UNSET_COMPLEX_POLYMER_INTERFACE_ID
  );
}

export function isComplexPolymerImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedComplexPolymerInterface | DraftImplementedComplexPolymerInterface {
  return isComplexPolymerInterfaceId(implementedInterface.interfaceId);
}

export function isOligoConjugateImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedOligoConjugateInterface | DraftImplementedOligoConjugateInterface {
  return implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID;
}

export function isOligoDuplexImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedOligoDuplexInterface | DraftImplementedOligoDuplexInterface {
  return implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID;
}

export function implementedInterfaceToComplexPolymerType(
  implementedInterface?: ImplementedStaticSchemaInterface | null
): ComplexPolymerType | null {
  if (
    implementedInterface == null ||
    !isComplexPolymerImplementedInterface(implementedInterface) ||
    implementedInterface.interfaceId === UNSET_COMPLEX_POLYMER_INTERFACE_ID
  ) {
    return null;
  }
  return COMPLEX_POLYMER_INTERFACE_API_ID_TO_TYPE[implementedInterface.interfaceId];
}

export function complexPolymerTypeToSchemaInterfaceId(
  complexPolymerType: ComplexPolymerType
): ComplexPolymerInterfaceApiId {
  return COMPLEX_POLYMER_TYPE_TO_INTERFACE_API_ID[complexPolymerType];
}

export function isCompleteImplementedComplexPolymerInterface(
  implementedInterface: DraftImplementedComplexPolymerInterface
): implementedInterface is ImplementedComplexPolymerInterface {
  if (implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.GENERIC_COMPLEX_POLYMER_ID) {
    return true;
  }
  if (implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID) {
    return (
      implementedInterface.oligoSchemaFieldSpec != null &&
      implementedInterface.conjugatesSchemaFieldSpecs != null &&
      implementedInterface.conjugatesSchemaFieldSpecs.length > 0
    );
  }
  if (implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID) {
    return (
      implementedInterface.forwardStrandSchemaFieldSpec != null &&
      implementedInterface.reverseStrandSchemaFieldSpec != null &&
      implementedInterface.conjugatesSchemaFieldSpecs != null
    );
  }

  return false;
}

const _SCHEMA_INTERFACE_GQL_TYPENAME_TO_COMPLEX_POLYMER_TYPE: {
  [k in ComplexPolymerSchemaInterfaceGQL['__typename']]: ComplexPolymerType | null;
} = {
  GenericComplexPolymerSchemaConfigNode: ComplexPolymerType.GENERIC,
  OligoConjugateSchemaConfigNode: ComplexPolymerType.OLIGO_CONJUGATE,
  OligoDuplexSchemaConfigNode: ComplexPolymerType.OLIGO_DUPLEX,
};

export function graphqlSchemaInterfaceToComplexPolymerType(schemaInterface: {
  __typename: ComplexPolymerSchemaInterfaceGQL['__typename'];
}): ComplexPolymerType {
  const {__typename} = schemaInterface;
  const type = _SCHEMA_INTERFACE_GQL_TYPENAME_TO_COMPLEX_POLYMER_TYPE[__typename];
  assert(type != null, 'Expected to find a matching complex polymer type.');
  return type;
}
